import { useState, useMemo, useCallback, useEffect } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SettingsHeader } from './components/settings-header';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { InfoCard } from '../../../../components/info-card';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/shadcn/tooltip';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useRelativitySyncProgress from '@/api/hooks/useRelativitySyncProgress';
import { APIBaseChronos } from '@/api/hosts';
import useBuildRelativityParentRelationships, {
  type BuildRelativityParentRelationshipsResponse,
} from '@/api/mutations/useBuildRelativityParentRelationships';
import useStartRelativitySync from '@/api/mutations/useStartRelativitySync';
import useGetConfiguredRelativityWorkspaces from '@/api/queries/useGetConfiguredRelativityWorkspaces';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import useGetRelativitySyncPreview from '@/api/queries/useGetRelativitySyncPreview';
import { PermissionGate } from '@/hooks/useHasPermission';
import { useRelativityAuth } from '@/hooks/useRelativityAuth';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Progress } from '@/new/components/shadcn/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';

export function RelativitySync() {
  const { matterId } = useParams();
  const { accessToken } = useAuthInfo();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | undefined>();
  const [activeJobId, setActiveJobId] = useState<string | undefined>();
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [relationshipBuildResult, setRelationshipBuildResult] =
    useState<BuildRelativityParentRelationshipsResponse | null>(null);

  // Check Relativity authentication status
  const { isAuthenticated, isLoading: isLoadingAuth, isConnecting, connectRelativity } = useRelativityAuth();

  // Fetch matter data to check if it's linked to a Relativity workspace
  const { data: matter } = useGetMatterV2(matterId);

  // Fetch configured workspaces (only if authenticated)
  const { data: workspaces, isLoading: isLoadingWorkspaces } = useGetConfiguredRelativityWorkspaces({
    enabled: isAuthenticated,
  });

  // Check if matter is linked to a specific workspace
  const linkedWorkspaceId = matter?.relativityWorkspaceId;
  const linkedWorkspace = useMemo(() => {
    if (!linkedWorkspaceId || !workspaces) return null;
    return workspaces.find((w) => w.workspaceId === linkedWorkspaceId);
  }, [linkedWorkspaceId, workspaces]);

  // Auto-select the linked workspace when available
  useEffect(() => {
    if (linkedWorkspaceId && !selectedWorkspaceId) {
      setSelectedWorkspaceId(linkedWorkspaceId);
    }
  }, [linkedWorkspaceId, selectedWorkspaceId]);

  useEffect(() => {
    setRelationshipBuildResult(null);
  }, [selectedWorkspaceId]);

  // Fetch preview when workspace selected
  const {
    data: preview,
    isLoading: isLoadingPreview,
    refetch: refetchPreview,
  } = useGetRelativitySyncPreview(matterId, selectedWorkspaceId, {
    enabled: !!selectedWorkspaceId,
  });

  // Start sync mutation
  const { mutateAsync: startSync, isLoading: isStarting } = useStartRelativitySync();
  const { mutateAsync: buildRelationships, isLoading: isBuildingRelationships } =
    useBuildRelativityParentRelationships();

  // SSE progress tracking
  const { progress, complete, error: progressError } = useRelativitySyncProgress(activeJobId);

  // Calculate progress percentage
  const progressPercentage = useMemo(() => {
    if (complete) return 100;
    if (!progress || progress.totalFiles === 0) return 0;
    return Math.round((progress.processedFiles / progress.totalFiles) * 100);
  }, [progress, complete]);

  const handleStartSync = async () => {
    if (!matterId || !selectedWorkspaceId) {
      toast.error('Please select a workspace');
      return;
    }

    try {
      const result = await startSync({ matterId, workspaceId: selectedWorkspaceId });
      trackCustomEvent(TrackingEventsV2.RelativitySync, {
        matterId,
        workspaceId: selectedWorkspaceId,
        filesFound: result.filesFound,
        jobId: result.jobId,
      });
      setActiveJobId(result.jobId);
      toast.success(`Started syncing metadata for ${result.filesFound.toLocaleString()} files`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start sync');
    }
  };

  const handleBuildRelationships = async () => {
    if (!matterId || !selectedWorkspaceId) {
      toast.error('Please select a workspace');
      return;
    }

    setRelationshipBuildResult(null);
    try {
      const result = await buildRelationships({ matterId, workspaceId: selectedWorkspaceId });
      trackCustomEvent(TrackingEventsV2.RelativityBuildParentChildRelationships, {
        matterId,
        workspaceId: selectedWorkspaceId,
        newRelationshipsCreated: result.newRelationshipsCreated,
        existingRelationshipsSkipped: result.existingRelationshipsSkipped,
      });
      setRelationshipBuildResult(result);
      const newRelationshipsLabel = result.newRelationshipsCreated === 1 ? 'relationship' : 'relationships';
      const skippedRelationshipsLabel = result.existingRelationshipsSkipped === 1 ? 'relationship' : 'relationships';
      toast.success(
        `Built ${result.newRelationshipsCreated.toLocaleString()} new ${newRelationshipsLabel}; skipped ${result.existingRelationshipsSkipped.toLocaleString()} existing ${skippedRelationshipsLabel}.`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to build relationships');
    }
  };

  const handleReset = () => {
    setActiveJobId(undefined);
    refetchPreview();
  };

  const handleDownloadReport = useCallback(async () => {
    if (!activeJobId || !accessToken) return;

    setIsDownloadingReport(true);
    try {
      const response = await fetch(`${APIBaseChronos}/api/relativity/sync/${activeJobId}/report`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download report');
      }

      // Get the filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'relativity-sync-report.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          filename = match[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to download report');
    } finally {
      setIsDownloadingReport(false);
    }
  }, [activeJobId, accessToken]);

  if (!matterId) return null;

  // Determine subheading based on state
  const subheading = activeJobId ? (
    <p className="text-sm text-gray-500">{complete ? 'Sync complete' : 'Syncing metadata from Relativity...'}</p>
  ) : (
    <p className="text-sm text-gray-500">
      Sync metadata from Relativity. This will update the metadata for all documents that were originally imported using
      the Relativity import feature with the latest values from your Relativity workspace. Only fields mapped in the{' '}
      <Link
        to="/relativity/field-mappings"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-600"
      >
        mapping configurator
        <Icon className="lucide--arrow-up-right" />
      </Link>{' '}
      will be imported.
    </p>
  );

  // Render content based on state
  const renderContent = () => {
    // Loading auth state
    if (isLoadingAuth) {
      return (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Icon className="lucide--loader-2 h-4 w-4 animate-spin" />
          Checking Relativity connection...
        </div>
      );
    }

    // Not authenticated - show connect prompt
    if (!isAuthenticated) {
      return (
        <div className="max-w-2xl">
          <SettingsHeader
            title="Connect to Relativity"
            subheading="To sync metadata from Relativity, you need to connect your Relativity account first. This will open a
                    new window to authenticate with Relativity."
            className="mb-0"
          />
          <PermissionGate
            permission={Permission.RelativityImport}
            uiMode="disabled"
            disabledMessage="You do not have permission to connect to a Relativity account"
          >
            <Button variant="primary" className="mt-4" onClick={connectRelativity} disabled={isConnecting}>
              {isConnecting ? (
                <>
                  <Icon className="lucide--loader-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Icon className="lucide--external-link" />
                  Connect to Relativity
                </>
              )}
            </Button>
          </PermissionGate>
        </div>
      );
    }

    // Active sync job - show progress/completion
    if (activeJobId) {
      return (
        <div className="max-w-2xl space-y-6">
          {/* Progress bar */}
          <div className="space-y-3.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {complete
                  ? 'Completed'
                  : progress?.status === 'preparing'
                    ? progress.preparingMessage || 'Preparing...'
                    : `Processing: ${(progress?.processedFiles ?? 0).toLocaleString()} / ${(progress?.totalFiles ?? 0).toLocaleString()} files`}
              </span>
              <span className="font-medium text-gray-900">{progressPercentage}%</span>
            </div>

            <Progress value={progressPercentage} className="h-2" />
          </div>

          {/* Stats */}
          <div className="flex max-w-md gap-12 rounded-lg border bg-gray-50 p-4 px-5">
            <div className="basis-1/3">
              <div className="text-2xl font-semibold text-gray-700">
                {(complete?.matched ?? progress?.matched ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Synced</div>
            </div>
            <div className="basis-1/3">
              <div className="text-2xl font-semibold text-gray-700">
                {(complete?.unmatched ?? progress?.unmatched ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Unmatched</div>
            </div>
            <div className="basis-1/3">
              <div className="text-2xl font-semibold text-gray-700">
                {(complete?.failed ?? progress?.failed ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>

          {/* Completion details */}
          {complete && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button onClick={handleDownloadReport} disabled={isDownloadingReport}>
                  {isDownloadingReport ? (
                    <>
                      <Icon className="lucide--loader-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Icon className="lucide--download" />
                      Download Report
                    </>
                  )}
                </Button>
                <Button onClick={handleReset}>Done</Button>
              </div>
            </div>
          )}

          {/* Error state */}
          {progressError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700">{progressError}</p>
              <Button variant="outline" className="mt-2" onClick={handleReset}>
                Try Again
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Default setup view
    return (
      <div className="max-w-2xl space-y-6 pb-12">
        {/* Workspace selector */}
        <div className="space-y-2">
          {isLoadingWorkspaces ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Icon className="lucide--loader-2 h-4 w-4 animate-spin" />
              Loading workspaces...
            </div>
          ) : linkedWorkspaceId ? (
            // Matter is linked to a specific workspace - show fixed workspace info
            <div className="flex w-fit items-center gap-2.5 text-sm">
              <span className="size-1.5 rounded-full bg-green-500"></span>
              <span className="text-gray-700">
                Syncing from{' '}
                <span className="font-semibold">
                  {linkedWorkspace?.workspaceName ?? `Workspace ${linkedWorkspaceId}`}
                </span>
              </span>
            </div>
          ) : workspaces && workspaces.length > 0 ? (
            <Select
              value={selectedWorkspaceId?.toString()}
              onValueChange={(value) => setSelectedWorkspaceId(parseInt(value, 10))}
            >
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Select a workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map((workspace) => (
                  <SelectItem key={workspace.workspaceId} value={workspace.workspaceId.toString()}>
                    {workspace.workspaceName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <p className="text-sm text-yellow-700">
                No Relativity workspaces with field mappings configured. Please configure field mappings first in the
                Relativity integration settings.
              </p>
            </div>
          )}
        </div>

        {/* Preview section */}
        {selectedWorkspaceId && (
          <div className="space-y-4">
            {isLoadingPreview ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Icon className="lucide--loader-2 h-4 w-4 animate-spin" />
                Checking files...
              </div>
            ) : preview ? (
              <>
                {/* Files count */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 px-5">
                  <div className="text-2xl font-semibold text-gray-900">
                    {preview.eligibleForReSync.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">
                    files ready to sync
                    {preview.skippedNoArtifactId > 0 && (
                      <span className="inline-flex items-center gap-1">
                        . {preview.skippedNoArtifactId.toLocaleString()} file(s) will be skipped due to not being linked
                        to a Relativity document{' '}
                        <Tooltip>
                          <TooltipTrigger>
                            <Icon className="lucide--circle-question-mark text-gray-500" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[25rem]" align="center">
                            To sync a document's metadata, it must have an associated <strong>Artifact ID</strong>. This
                            is automatically assigned when importing directly from Relativity. Contact{' '}
                            <a href="mailto:support@wexler.ai" className="text-blue-500">
                              support@wexler.ai
                            </a>{' '}
                            for more information.
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    )}
                  </div>
                </div>

                {/* Field mapping status */}
                {!preview.fieldMappingConfigured && (
                  <InfoCard>
                    <p>
                      Field mappings aren't configured for this Relativity workspace. Configure field mappings in{' '}
                      <a
                        href="/relativity/field-mappings"
                        target="_blank"
                        className="inline-flex items-center gap-1 text-blue-500 duration-150 hover:text-blue-600"
                      >
                        organisation settings <Icon className="lucide--arrow-up-right" />
                      </a>{' '}
                      to enable syncing.
                    </p>
                  </InfoCard>
                )}

                {/* Start sync button */}
                {preview.eligibleForReSync > 0 && preview.fieldMappingConfigured && (
                  <div className="pt-2">
                    <Button onClick={handleStartSync} disabled={isStarting}>
                      {isStarting ? (
                        <>
                          <Icon className="lucide--loader animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <Icon className="lucide--refresh-cw" />
                          Start Sync
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {preview.eligibleForReSync === 0 && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <span className="text-sm text-gray-700">
                      No files in this matter have Relativity metadata to re-sync. Metadata is added when documents are
                      imported from Relativity.
                    </span>
                  </div>
                )}

                <hr className="my-8 border-gray-200" />

                {/* Parent/child relationship builder */}
                <div className="space-y-6">
                  <SettingsHeader
                    title="Build Parent/child Relationships"
                    subheading={
                      <p>
                        Wexler uses the configured parent identifier field to match child documents to parent Control
                        Numbers. Configure this in{' '}
                        <Link
                          to="/relativity/field-mappings"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-600"
                        >
                          Relativity Field Mappings
                          <Icon className="lucide--arrow-up-right" />
                        </Link>
                        .
                      </p>
                    }
                  />

                  <div className="space-y-4">
                    {relationshipBuildResult && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                          <div className="text-lg font-semibold text-green-700">
                            {relationshipBuildResult.newRelationshipsCreated.toLocaleString()}
                          </div>
                          <div className="text-xs text-green-700">New relationships created</div>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="text-lg font-semibold text-gray-900">
                            {relationshipBuildResult.existingRelationshipsSkipped.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-600">Existing relationships skipped</div>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleBuildRelationships}
                      disabled={isBuildingRelationships || !preview.fieldMappingConfigured}
                    >
                      {isBuildingRelationships ? (
                        <>
                          <Icon className="lucide--loader animate-spin" />
                          Building...
                        </>
                      ) : (
                        <>
                          <Icon className="lucide--network" />
                          Build Relationships
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <SettingsHeader title="Relativity Metadata Sync" subheading={subheading} />
      {renderContent()}
    </>
  );
}
